/**
 * DriverProgressManager handles all driver skill state operations.
 *
 * Provides a focused API for driver skill management while keeping
 * actual state in GameState.state.driver for save compatibility.
 *
 * Driver skills:
 * - Fuel Tank: Maximum offline simulation time (30min to 12hr)
 * - Scrap Bonus: Bonus wavedust from scrapping (1.0x to 1.5x)
 * - Item Upgrade Bonus: Item upgrade effect scaling (1.0x to 1.25x)
 * - Wave Luck: Wave quality bonus (1.0x to 1.5x)
 */
export class DriverProgressManager {
    /**
     * Driver skill upgrade costs (shared by all skills).
     * 10 levels total, indexed by current level (0-9).
     */
    static DRIVER_SKILL_COSTS = [25, 50, 100, 175, 300, 500, 850, 1400, 2300, 3800];

    /**
     * Maximum level for all driver skills.
     */
    static MAX_LEVEL = 10;

    /**
     * @param {GameState} gameState - Reference to main game state
     */
    constructor(gameState) {
        this.gameState = gameState;
    }

    /**
     * Get the driver state object (shorthand).
     * @returns {Object} Driver skill state
     */
    get driver() {
        return this.gameState.state.driver;
    }

    // =========================================================================
    // FUEL TANK - Maximum fuel capacity for offline driving
    // =========================================================================

    /**
     * Get current fuel tank level.
     * @returns {number} Current level (0-10)
     */
    getFuelTankLevel() {
        return this.driver?.fuelTank?.level || 0;
    }

    /**
     * Get fuel tank capacity in seconds based on level.
     * Level 0: 30 minutes, Level 10: 12 hours.
     * @returns {number} Capacity in seconds
     */
    getFuelTankCapacity() {
        const baseTime = 30 * 60; // 30 minutes in seconds
        const maxTime = 12 * 60 * 60; // 12 hours in seconds
        const level = this.getFuelTankLevel();
        const maxLevel = DriverProgressManager.MAX_LEVEL;

        // Linear scaling from 30 min to 12 hours
        return baseTime + (maxTime - baseTime) * (level / maxLevel);
    }

    /**
     * Get fuel tank upgrade cost for next level.
     * @returns {number|null} Cost in miles, or null if maxed
     */
    getFuelTankCost() {
        const level = this.getFuelTankLevel();
        if (level >= DriverProgressManager.MAX_LEVEL) return null;
        return DriverProgressManager.DRIVER_SKILL_COSTS[level];
    }

    /**
     * Upgrade fuel tank capacity.
     * @returns {boolean} True if upgrade succeeded
     */
    upgradeFuelTank() {
        const cost = this.getFuelTankCost();
        if (cost === null || this.gameState.state.miles < cost) return false;

        this.gameState.state.miles -= cost;
        console.log(`[DriverProgressManager] Spent ${cost} miles on Fuel Tank`);

        // Ensure driver state structure exists
        if (!this.gameState.state.driver) this.gameState.state.driver = {};
        if (!this.gameState.state.driver.fuelTank) this.gameState.state.driver.fuelTank = { level: 0 };

        this.gameState.state.driver.fuelTank.level++;
        this.gameState.save();
        return true;
    }

    // =========================================================================
    // SCRAP BONUS - Increases wavedust from scrapping waves/items
    // =========================================================================

    /**
     * Get current scrap bonus level.
     * @returns {number} Current level (0-10)
     */
    getScrapBonusLevel() {
        return this.driver?.scrapBonus?.level || 0;
    }

    /**
     * Get scrap bonus upgrade cost for next level.
     * @returns {number|null} Cost in miles, or null if maxed
     */
    getScrapBonusCost() {
        const level = this.getScrapBonusLevel();
        if (level >= DriverProgressManager.MAX_LEVEL) return null;
        return DriverProgressManager.DRIVER_SKILL_COSTS[level];
    }

    /**
     * Upgrade scrap bonus.
     * @returns {boolean} True if upgrade succeeded
     */
    upgradeScrapBonus() {
        const cost = this.getScrapBonusCost();
        if (cost === null || this.gameState.state.miles < cost) return false;

        this.gameState.state.miles -= cost;
        console.log(`[DriverProgressManager] Spent ${cost} miles on Scrap Bonus`);

        // Ensure driver state structure exists
        if (!this.gameState.state.driver) this.gameState.state.driver = {};
        if (!this.gameState.state.driver.scrapBonus) this.gameState.state.driver.scrapBonus = { level: 0 };

        this.gameState.state.driver.scrapBonus.level++;
        this.gameState.save();
        return true;
    }

    /**
     * Get scrap bonus multiplier (1.0 to 1.5).
     * @param {boolean} noisy - If true, applies +/-10% random variance
     * @returns {number} Multiplier value
     */
    getScrapBonusMultiplier(noisy = false) {
        const level = this.getScrapBonusLevel();
        const bonusPortion = (level / DriverProgressManager.MAX_LEVEL) * 0.5; // 0.0 to 0.5

        if (noisy && level > 0) {
            // Apply variance only to the bonus portion, not the base
            // This ensures multiplier never goes below 1.0
            const variance = (Math.random() * 0.2 - 0.1); // +/-10%
            return 1 + bonusPortion * (1 + variance);
        }
        return 1 + bonusPortion;
    }

    // =========================================================================
    // ITEM UPGRADE BONUS - Increases item upgrade effect scaling
    // =========================================================================

    /**
     * Get current item upgrade bonus level.
     * @returns {number} Current level (0-10)
     */
    getItemUpgradeBonusLevel() {
        return this.driver?.itemUpgradeBonus?.level || 0;
    }

    /**
     * Get item upgrade bonus upgrade cost for next level.
     * @returns {number|null} Cost in miles, or null if maxed
     */
    getItemUpgradeBonusCost() {
        const level = this.getItemUpgradeBonusLevel();
        if (level >= DriverProgressManager.MAX_LEVEL) return null;
        return DriverProgressManager.DRIVER_SKILL_COSTS[level];
    }

    /**
     * Upgrade item upgrade bonus.
     * @returns {boolean} True if upgrade succeeded
     */
    upgradeItemUpgradeBonus() {
        const cost = this.getItemUpgradeBonusCost();
        if (cost === null || this.gameState.state.miles < cost) return false;

        this.gameState.state.miles -= cost;
        console.log(`[DriverProgressManager] Spent ${cost} miles on Item Upgrade Bonus`);

        // Ensure driver state structure exists
        if (!this.gameState.state.driver) this.gameState.state.driver = {};
        if (!this.gameState.state.driver.itemUpgradeBonus) this.gameState.state.driver.itemUpgradeBonus = { level: 0 };

        this.gameState.state.driver.itemUpgradeBonus.level++;
        this.gameState.save();
        return true;
    }

    /**
     * Get item upgrade bonus multiplier (1.0 to 1.25).
     * This multiplies the per-level bonus items get when upgraded.
     * @returns {number} Multiplier value
     */
    getItemUpgradeBonusMultiplier() {
        const level = this.getItemUpgradeBonusLevel();
        return 1 + (level / DriverProgressManager.MAX_LEVEL) * 0.25; // 1.0 to 1.25
    }

    // =========================================================================
    // WAVE LUCK - Increases chance of better tier waves
    // =========================================================================

    /**
     * Get current wave luck level.
     * @returns {number} Current level (0-10)
     */
    getWaveLuckLevel() {
        return this.driver?.waveLuck?.level || 0;
    }

    /**
     * Get wave luck upgrade cost for next level.
     * @returns {number|null} Cost in miles, or null if maxed
     */
    getWaveLuckCost() {
        const level = this.getWaveLuckLevel();
        if (level >= DriverProgressManager.MAX_LEVEL) return null;
        return DriverProgressManager.DRIVER_SKILL_COSTS[level];
    }

    /**
     * Upgrade wave luck.
     * @returns {boolean} True if upgrade succeeded
     */
    upgradeWaveLuck() {
        const cost = this.getWaveLuckCost();
        if (cost === null || this.gameState.state.miles < cost) return false;

        this.gameState.state.miles -= cost;
        console.log(`[DriverProgressManager] Spent ${cost} miles on Wave Luck`);

        // Ensure driver state structure exists
        if (!this.gameState.state.driver) this.gameState.state.driver = {};
        if (!this.gameState.state.driver.waveLuck) this.gameState.state.driver.waveLuck = { level: 0 };

        this.gameState.state.driver.waveLuck.level++;
        this.gameState.save();
        return true;
    }

    /**
     * Get wave luck bonus (0.0 to 0.5).
     * This is applied as a multiplier to wave quality: quality *= (1 + bonus).
     * @returns {number} Bonus value (0.0 to 0.5)
     */
    getWaveLuckBonus() {
        const level = this.getWaveLuckLevel();
        return (level / DriverProgressManager.MAX_LEVEL) * 0.50; // 0.0 to 0.50
    }

    /**
     * Get wave luck as a display multiplier (1.0x to 1.5x).
     * @returns {number} Multiplier value
     */
    getWaveLuckMultiplier() {
        return 1 + this.getWaveLuckBonus();
    }
}
